import {
  actionRemboursement,
  beneficiaireCreation,
  beneficiaireModification,
  couvertureCreation,
  type EtatSoin,
  formaterMontant,
  LIBELLES_NATURE_SOIN,
  lienPieceSoin,
  NATURES_SOIN,
  PAYEURS_SANTE,
  PORTAILS_PREUVE_SANTE,
  remboursementCreation,
  ROLES_PIECE_SOIN,
  soinCreation,
  soinModification,
} from '@admin-pmvb/core'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import {
  api,
  corps,
  ErreurApi,
  type FicheSoin,
  lire,
  type Membre,
  requetes,
  type Soin,
} from '../api'
import { type Champ, Formulaire } from '../composants/Formulaire'
import { Icone } from '../composants/Icone'
import { Ligne, Section, Vide } from '../composants/Section'
import { EntetePage, Filtre, Onglets } from '../composants/Ui'
import {
  jourCourt,
  LIBELLES_ETAT_SOIN,
  LIBELLES_ORIGINE_SOIN,
  LIBELLES_PAYEUR_SANTE,
  LIBELLES_ROLE_PIECE_SOIN,
  LIBELLES_STATUT_REMBOURSEMENT,
  TON_ETAT_SOIN,
} from '../libelles'
import { confirmer } from '../composants/confirmation'

// Santé du foyer : un tableau par membre (soins, remboursements Sécu et mutuelle, reste à
// charge), les membres et leurs couvertures, et la fiche d'un soin. Le dossier pour l'autre
// parent se prépare ici ; il part de la messagerie d'un humain (boîte d'envoi).

const NATURES = NATURES_SOIN.map((n) => ({ valeur: n, libelle: LIBELLES_NATURE_SOIN[n] }))
const PAYEURS = PAYEURS_SANTE.map((p) => ({ valeur: p, libelle: LIBELLES_PAYEUR_SANTE[p] }))
const ROLES = ROLES_PIECE_SOIN.map((r) => ({ valeur: r, libelle: LIBELLES_ROLE_PIECE_SOIN[r] }))

const montant = (cts: number | null, devise = 'EUR') =>
  cts === null ? 'à préciser' : formaterMontant(cts, devise)

export function BadgeEtatSoin({ etat }: { etat: EtatSoin }) {
  return <span className={`badge ${TON_ETAT_SOIN[etat]}`}>{LIBELLES_ETAT_SOIN[etat]}</span>
}

function messageErreur(e: unknown) {
  return e instanceof ErreurApi ? e.message : 'Action impossible.'
}

function useFoyer() {
  const entites = useQuery(requetes.entites)
  return entites.data?.find((e) => e.type === 'foyer' && !e.archivee) ?? null
}

function champsSoin(membres: Membre[]): Champ[] {
  return [
    {
      nom: 'beneficiaireId',
      libelle: 'Pour',
      type: 'choix',
      options: membres.filter((m) => m.actif).map((m) => ({ valeur: m.id, libelle: m.prenom })),
    },
    { nom: 'date', libelle: 'Date', type: 'date', requis: true },
    { nom: 'heure', libelle: 'Heure', type: 'heure' },
    { nom: 'nature', libelle: 'Nature', type: 'choix', requis: true, options: NATURES },
    { nom: 'libelle', libelle: 'Précision', type: 'texte', aide: '« Semestre 3 », « verres ».' },
    { nom: 'praticien', libelle: 'Praticien', type: 'texte' },
    { nom: 'montantPayeCts', libelle: 'Montant payé (€)', type: 'montant' },
    {
      nom: 'avanceParAutreParent',
      libelle: 'Avancé par l’autre parent',
      type: 'booleen',
      aide: 'Ce que notre mutuelle rembourse lui revient.',
    },
    { nom: 'note', libelle: 'Note', type: 'zone', lignes: 2 },
  ]
}

export function Sante() {
  const client = useQueryClient()
  const navigate = useNavigate()
  const foyer = useFoyer()
  const membres = useQuery(requetes.membres)
  const [membre, setMembre] = useState<string>('tous')
  const [vue, setVue] = useState<'ouverts' | 'tous'>('ouverts')
  const [annee, setAnnee] = useState('')
  const soins = useQuery(
    requetes.soins({
      ...(membre !== 'tous' && membre !== 'a_preciser' && { beneficiaireId: membre }),
      ...(vue === 'ouverts' && { ouverts: 'true' as const }),
      ...(annee && { annee }),
    }),
  )
  const [ajout, setAjout] = useState(false)
  const [choisis, setChoisis] = useState<Set<string>>(new Set())
  const [message, setMessage] = useState<{ texte: string; tacheId?: string } | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)

  const liste = (soins.data ?? []).filter(
    (s) => membre !== 'a_preciser' || s.beneficiaireId === null,
  )
  const somme = (f: (s: Soin) => number | null) => liste.reduce((t, s) => t + (f(s) ?? 0), 0)
  const listeMembres = membres.data ?? []
  // Un dossier ne part pas sans ses pièces (décision de Pierre-Marie du 26/10/2026).
  const incompletsChoisis = liste.filter(
    (s) => choisis.has(s.id) && s.preuvesManquantes.length > 0,
  ).length
  const anneeCourante = new Date().getFullYear()

  async function preparerDossier() {
    setErreur(null)
    try {
      const r = await lire(api.soins.dossier.$post({ json: { soinIds: [...choisis] } }))
      setChoisis(new Set())
      setMessage({
        texte: `Dossier préparé : ${formaterMontant(r.totalCts)} à demander. Le courriel attend dans la boîte d’envoi, le PDF à joindre est dans les pièces.${r.illisibles.length > 0 ? ` Écartées : ${r.illisibles.join(', ')}.` : ''}`,
        tacheId: r.tacheId,
      })
      await client.invalidateQueries()
    } catch (e) {
      setErreur(messageErreur(e))
    }
  }

  if (!foyer)
    return (
      <main>
        <EntetePage surtitre="Foyer" titre="Santé" />
        <Vide>La santé se suit sur l’entité du foyer, réservée aux administrateurs.</Vide>
      </main>
    )

  return (
    <main>
      <EntetePage
        surtitre="Foyer"
        titre="Santé"
        action={{ icone: 'add', libelle: 'Nouveau soin', onClick: () => setAjout(true) }}
      />
      <div className="pile" style={{ gap: 16 }}>
        <Onglets
          defilants
          valeur={membre}
          onChange={(v) => {
            setMembre(v)
            setChoisis(new Set())
          }}
          onglets={[
            { valeur: 'tous', libelle: 'Tout le foyer' },
            ...listeMembres
              .filter((m) => m.actif)
              .map((m) => ({ valeur: m.id, libelle: m.prenom })),
            { valeur: 'a_preciser', libelle: 'À préciser' },
          ]}
        />
        <div className="rangee-filtres" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Filtre
            icone="filter_list"
            libelle="Soins"
            valeur={vue}
            onChange={(v) => setVue(v as 'ouverts' | 'tous')}
            options={[
              { valeur: 'ouverts', libelle: 'À suivre' },
              { valeur: 'tous', libelle: 'Tous les soins' },
            ]}
          />
          <Filtre
            icone="calendar_month"
            libelle="Année"
            valeur={annee}
            onChange={setAnnee}
            options={[
              { valeur: '', libelle: 'Toutes les années' },
              ...[0, 1, 2].map((n) => ({
                valeur: String(anneeCourante - n),
                libelle: String(anneeCourante - n),
              })),
            ]}
          />
        </div>
        <div
          className="tuiles"
          style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))' }}
        >
          <div className="tuile">
            <span>Payé</span>
            <span className="mono">{formaterMontant(somme((s) => s.montantPayeCts))}</span>
          </div>
          <div className="tuile entree">
            <span>Sécu et mutuelle</span>
            <span className="mono">{formaterMontant(somme((s) => s.rembourseCts))}</span>
          </div>
          <div className="tuile entree">
            <span>Complément reçu</span>
            <span className="mono">{formaterMontant(somme((s) => s.complementCts))}</span>
          </div>
          <div className="tuile">
            <span>Reste à charge</span>
            <span className="mono">{formaterMontant(somme((s) => s.resteACts))}</span>
          </div>
        </div>
        {ajout && (
          <section className="carte">
            <h2>Nouveau soin</h2>
            <Formulaire
              champs={champsSoin(listeMembres)}
              initial={{
                beneficiaireId: membre !== 'tous' && membre !== 'a_preciser' ? membre : null,
                nature: 'consultation',
              }}
              onAnnuler={() => setAjout(false)}
              onValider={async (v) => {
                const s = await lire(
                  api.soins.$post({ json: corps(soinCreation, { ...v, entiteId: foyer.id }) }),
                )
                await client.invalidateQueries({ queryKey: ['sante'] })
                setAjout(false)
                await navigate({ to: '/sante/soins/$id', params: { id: s.id } })
              }}
            />
          </section>
        )}
        {message && (
          <p className="discret">
            {message.texte}{' '}
            {message.tacheId && (
              <Link to="/taches/$id" params={{ id: message.tacheId }}>
                Ouvrir la tâche
              </Link>
            )}
          </p>
        )}
        {erreur && <p className="alerte">{erreur}</p>}
        <section className="carte carte-flush">
          {choisis.size > 0 && (
            <div className="barre-selection">
              <Icone nom="check_box" taille={19} />
              <strong>{choisis.size} sélectionnés</strong>
              <button type="button" className="bouton-texte" onClick={() => setChoisis(new Set())}>
                Désélectionner
              </button>
              <button
                type="button"
                className="bouton"
                disabled={incompletsChoisis > 0}
                onClick={preparerDossier}
              >
                <Icone nom="forward_to_inbox" taille={17} />
                Préparer le dossier pour l’autre parent
              </button>
              {incompletsChoisis > 0 && (
                <span className="discret">
                  {incompletsChoisis > 1
                    ? `${incompletsChoisis} soins sans toutes leurs pièces`
                    : 'Un soin sans toutes ses pièces'}{' '}
                  : voir sa fiche.
                </span>
              )}
            </div>
          )}
          {soins.isPending && <p className="discret vide">Chargement…</p>}
          {soins.data && liste.length === 0 && <p className="discret vide">Rien ici.</p>}
          {liste.map((s) => (
            <div key={s.id} className="ligne">
              <span className="case" style={{ width: 20 }}>
                {s.etat === 'a_demander' && (
                  <input
                    type="checkbox"
                    aria-label="Choisir pour le dossier"
                    checked={choisis.has(s.id)}
                    onChange={(e) =>
                      setChoisis((c) => {
                        const n = new Set(c)
                        if (e.target.checked) n.add(s.id)
                        else n.delete(s.id)
                        return n
                      })
                    }
                  />
                )}
              </span>
              <Link to="/sante/soins/$id" params={{ id: s.id }} className="ligne-contenu">
                <span className="ligne-titre">
                  <strong>
                    {s.beneficiaire ?? 'À préciser'} · {s.libelle ?? LIBELLES_NATURE_SOIN[s.nature]}
                  </strong>
                  <BadgeEtatSoin etat={s.etat} />
                </span>
                <span className="discret">
                  {[
                    `${jourCourt(s.date)}${s.heure ? ` ${s.heure}` : ''}`,
                    LIBELLES_NATURE_SOIN[s.nature],
                    s.praticien,
                    s.pieces.length > 0 && `${s.pieces.length} pièce(s)`,
                    s.etat === 'a_demander' &&
                      s.preuvesManquantes.length > 0 &&
                      `manque ${s.preuvesManquantes.map((m) => m.libelle).join(', ')}`,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              </Link>
              <div className="montant">
                <span>{montant(s.montantPayeCts, s.devise)}</span>
                {s.resteACts !== null && (
                  <span className="discret">reste {formaterMontant(s.resteACts, s.devise)}</span>
                )}
              </div>
            </div>
          ))}
        </section>
        <MembresSante foyerId={foyer.id} membres={listeMembres} />
      </div>
    </main>
  )
}

/**
 * Dossier incomplet : il ne part pas sans ses pièces (décision de Pierre-Marie du 26/10/2026).
 * Ce qui manque, et le collecteur qui rapporte chaque décompte, à lancer d'ici ; les décomptes
 * lus se rattachent seuls au soin, et le bouton se débloque.
 */
function PreuvesARecuperer({ s }: { s: FicheSoin }) {
  const client = useQueryClient()
  const collecteurs = useQuery(requetes.collecteurs).data ?? []
  const [occupe, setOccupe] = useState<string | null>(null)
  const [messages, setMessages] = useState<Record<string, string>>({})
  const manque = s.preuvesManquantes
  const utiles = collecteurs.filter(
    (c) =>
      c.entiteId === s.entiteId &&
      manque.some(
        (m) => m.cle !== 'facture' && PORTAILS_PREUVE_SANTE[m.cle].includes(c.fournisseur),
      ),
  )
  async function lancer(c: (typeof utiles)[number]) {
    setOccupe(c.id)
    try {
      await lire(api.portails[':id'].collecter.$post({ param: { id: c.id } }))
      setMessages((m) => ({
        ...m,
        [c.id]:
          c.releve === 'ordinateur'
            ? `lancé : « ${c.ordinateur ?? 'l’ordinateur'} » le prend s’il est allumé et Chrome ouvert.`
            : 'lancé.',
      }))
    } catch (e) {
      setMessages((m) => ({ ...m, [c.id]: messageErreur(e) }))
    }
    await client.invalidateQueries({ queryKey: ['portails'] })
    setOccupe(null)
  }
  return (
    <div className="pile" style={{ marginTop: 12 }}>
      <p className="alerte">
        Dossier incomplet : manque {manque.map((m) => m.libelle).join(', ')}. Il ne part pas sans.
      </p>
      {manque.some((m) => m.cle === 'facture') && (
        <p className="discret">La facture : la lier ci-dessous (« Lier une pièce »).</p>
      )}
      {utiles.map((c) => (
        <div key={c.id} className="boutons">
          <button
            type="button"
            className="bouton"
            disabled={!c.lancable || occupe !== null}
            onClick={() => void lancer(c)}
          >
            <Icone nom="play_arrow" />
            {occupe === c.id ? 'Lancement…' : `Lancer « ${c.libelle} »`}
          </button>
          <span className="discret">
            {messages[c.id] ?? (c.lancable ? '' : (c.sansReleve ?? 'à lancer depuis Collecteurs'))}
          </span>
        </div>
      ))}
      {manque.some((m) => m.cle !== 'facture') && utiles.length === 0 && (
        <p className="discret">Aucun collecteur ne les relève : les déposer dans Pièces.</p>
      )}
    </div>
  )
}

function MembresSante({ foyerId, membres }: { foyerId: string; membres: Membre[] }) {
  const client = useQueryClient()
  const tiers = useQuery(requetes.tiers).data ?? []
  const personnes = useQuery(requetes.personnes).data ?? []
  const rafraichir = () => client.invalidateQueries({ queryKey: ['sante'] })
  const parents = tiers.filter((t) => t.role === 'particulier')
  const organismes = tiers.filter((t) => t.role === 'organisme_sante')
  const champsMembre: Champ[] = [
    { nom: 'prenom', libelle: 'Prénom', type: 'texte', requis: true },
    { nom: 'nom', libelle: 'Nom', type: 'texte' },
    { nom: 'dateNaissance', libelle: 'Date de naissance', type: 'date' },
    {
      nom: 'personneId',
      libelle: 'Personne de l’appli',
      type: 'choix',
      aide: 'Ses rendez-vous d’agenda lui sont attribués.',
      options: personnes.map((p) => ({ valeur: p.id, libelle: p.nom })),
    },
    {
      nom: 'autreParentId',
      libelle: 'Autre parent',
      type: 'choix',
      aide: 'Tiers « particulier » à qui transmettre les pièces du complément.',
      options: parents.map((t) => ({ valeur: t.id, libelle: t.nom })),
    },
  ]
  const champsCouverture: Champ[] = [
    { nom: 'payeur', libelle: 'Qui rembourse', type: 'choix', requis: true, options: PAYEURS },
    {
      nom: 'organismeId',
      libelle: 'Organisme',
      type: 'choix',
      options: organismes.map((t) => ({ valeur: t.id, libelle: t.nom })),
    },
    { nom: 'libelle', libelle: 'Précision', type: 'texte' },
    { nom: 'rang', libelle: 'Ordre', type: 'entier', requis: true, aide: '1 : Sécurité sociale.' },
  ]
  return (
    <Section
      titre="Membres du foyer et couvertures"
      libelleAjout="Nouveau membre"
      ajout={(fermer) => (
        <Formulaire
          champs={champsMembre}
          onAnnuler={fermer}
          onValider={async (v) => {
            await lire(
              api.beneficiaires.$post({
                json: corps(beneficiaireCreation, { ...v, entiteId: foyerId }),
              }),
            )
            await rafraichir()
            fermer()
          }}
        />
      )}
    >
      <p className="discret">
        Organismes (CPAM, mutuelles) et autre parent se créent dans Tiers, rattachés au foyer.
      </p>
      {membres.length === 0 && <Vide>Aucun membre.</Vide>}
      {membres.map((m) => (
        <div key={m.id} className="bloc">
          <Ligne
            edition={(fermer) => (
              <Formulaire
                champs={[...champsMembre, { nom: 'actif', libelle: 'Suivi', type: 'booleen' }]}
                initial={m}
                onAnnuler={fermer}
                onValider={async (v) => {
                  await lire(
                    api.beneficiaires[':id'].$patch({
                      param: { id: m.id },
                      json: corps(beneficiaireModification, v),
                    }),
                  )
                  await rafraichir()
                  fermer()
                }}
              />
            )}
          >
            <strong>{m.prenom}</strong>
            <span className="discret">
              {' '}
              {[m.nom, m.autreParent && `autre parent : ${m.autreParent}`, !m.actif && 'plus suivi']
                .filter(Boolean)
                .join(' · ')}
            </span>
          </Ligne>
          <div className="sous-liste">
            {m.couvertures.map((c) => (
              <Ligne
                key={c.id}
                onSupprimer={async () => {
                  await lire(api.couvertures[':id'].$delete({ param: { id: c.id } }))
                  await rafraichir()
                }}
              >
                <span>
                  {c.rang}. {LIBELLES_PAYEUR_SANTE[c.payeur]}
                  {c.organisme && ` : ${c.organisme}`}
                  {c.libelle && ` (${c.libelle})`}
                  {!c.actif && ' · inactive'}
                </span>
              </Ligne>
            ))}
            <AjoutCouverture
              champs={champsCouverture}
              rang={m.couvertures.length + 1}
              onValider={async (v) => {
                await lire(
                  api.couvertures.$post({
                    json: corps(couvertureCreation, { ...v, beneficiaireId: m.id }),
                  }),
                )
                await rafraichir()
              }}
            />
          </div>
        </div>
      ))}
    </Section>
  )
}

function AjoutCouverture({
  champs,
  rang,
  onValider,
}: {
  champs: Champ[]
  rang: number
  onValider: (v: Record<string, unknown>) => Promise<void>
}) {
  const [ouvert, setOuvert] = useState(false)
  if (!ouvert)
    return (
      <button type="button" className="bouton-discret" onClick={() => setOuvert(true)}>
        Ajouter une couverture
      </button>
    )
  return (
    <Formulaire
      champs={champs}
      initial={{ rang }}
      onAnnuler={() => setOuvert(false)}
      onValider={async (v) => {
        await onValider(v)
        setOuvert(false)
      }}
    />
  )
}

export function SoinFiche({ id }: { id: string }) {
  const client = useQueryClient()
  const navigate = useNavigate()
  const foyer = useFoyer()
  const soin = useQuery(requetes.soin(id))
  const membres = useQuery(requetes.membres).data ?? []
  const tiers = useQuery(requetes.tiers).data ?? []
  const [edition, setEdition] = useState(false)
  const [erreur, setErreur] = useState<string | null>(null)
  const [message, setMessage] = useState<{ texte: string; tacheId?: string } | null>(null)
  const rafraichir = () => client.invalidateQueries()

  async function agir(f: () => Promise<unknown>) {
    setErreur(null)
    try {
      await f()
      await rafraichir()
    } catch (e) {
      setErreur(messageErreur(e))
    }
  }

  if (soin.isPending) return <main className="discret">Chargement…</main>
  if (!soin.data)
    return (
      <main>
        <Vide>Soin introuvable.</Vide>
      </main>
    )
  const s = soin.data
  return (
    <main>
      <Link to="/sante" className="lien-fleche">
        <Icone nom="arrow_back" />
        Santé
      </Link>
      <EntetePage
        surtitre={`${s.beneficiaire ?? 'Membre à préciser'} · ${LIBELLES_ORIGINE_SOIN[s.origine]}`}
        titre={`${s.libelle ?? LIBELLES_NATURE_SOIN[s.nature]}, ${jourCourt(s.date)}`}
      />
      <div className="pile" style={{ gap: 16 }}>
        <section className="carte">
          <div className="ligne-titre">
            <BadgeEtatSoin etat={s.etat} />
            <span className="discret">
              {[s.heure, LIBELLES_NATURE_SOIN[s.nature], s.praticien].filter(Boolean).join(' · ')}
            </span>
          </div>
          <div
            className="tuiles"
            style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', marginTop: 12 }}
          >
            <div className="tuile">
              <span>Payé{s.avanceParAutreParent ? ' (par l’autre parent)' : ''}</span>
              <span className="mono">{montant(s.montantPayeCts, s.devise)}</span>
            </div>
            <div className="tuile entree">
              <span>Sécu et mutuelle</span>
              <span className="mono">{formaterMontant(s.rembourseCts, s.devise)}</span>
            </div>
            <div className="tuile entree">
              <span>Complément</span>
              <span className="mono">{formaterMontant(s.complementCts, s.devise)}</span>
            </div>
            <div className="tuile">
              <span>Reste à charge</span>
              <span className="mono">{montant(s.resteACts, s.devise)}</span>
            </div>
          </div>
          {s.note && <p className="discret">{s.note}</p>}
          <div className="ligne-actions" style={{ justifyContent: 'flex-start', marginTop: 12 }}>
            {s.etat === 'a_demander' && s.autreParent && (
              <button
                type="button"
                className="bouton bouton-principal"
                disabled={s.preuvesManquantes.length > 0}
                onClick={() =>
                  agir(async () => {
                    const r = await lire(api.soins.dossier.$post({ json: { soinIds: [s.id] } }))
                    setMessage({
                      texte: `Dossier préparé pour ${s.autreParent!.nom} : ${formaterMontant(r.totalCts)}. Courriel dans la boîte d’envoi, PDF dans les pièces.${r.illisibles.length > 0 ? ` Écartées : ${r.illisibles.join(', ')}.` : ''}`,
                      tacheId: r.tacheId,
                    })
                  })
                }
              >
                <Icone nom="forward_to_inbox" />
                Préparer le dossier pour {s.autreParent.nom}
              </button>
            )}
            {s.etat === 'a_reverser' && (
              <button
                type="button"
                className="bouton"
                onClick={() =>
                  agir(() => lire(api.soins[':id'].reverser.$post({ param: { id: s.id } })))
                }
              >
                <Icone nom="north_east" />
                Reverser à l’autre parent
              </button>
            )}
            <button type="button" className="bouton-discret" onClick={() => setEdition(true)}>
              Modifier
            </button>
            <button
              type="button"
              className="bouton-discret"
              onClick={() =>
                agir(() =>
                  lire(
                    api.soins[':id'].$patch({
                      param: { id: s.id },
                      json: { sansSuite: !s.sansSuite },
                    }),
                  ),
                )
              }
            >
              {s.sansSuite ? 'Reprendre le suivi' : 'Sans suite'}
            </button>
            {s.origine === 'manuelle' && (
              <button
                type="button"
                className="bouton-discret"
                onClick={async () =>
                  (await confirmer('Supprimer ce soin ?')) &&
                  agir(async () => {
                    await lire(api.soins[':id'].$delete({ param: { id: s.id } }))
                    await navigate({ to: '/sante' })
                  })
                }
              >
                Supprimer
              </button>
            )}
          </div>
          {s.etat === 'a_demander' && s.autreParent && s.preuvesManquantes.length > 0 && (
            <PreuvesARecuperer s={s} />
          )}
          {message && (
            <p className="discret">
              {message.texte}{' '}
              {message.tacheId && (
                <Link to="/taches/$id" params={{ id: message.tacheId }}>
                  Ouvrir la tâche
                </Link>
              )}
            </p>
          )}
          {erreur && <p className="alerte">{erreur}</p>}
          {edition && (
            <Formulaire
              champs={champsSoin(membres)}
              initial={s}
              onAnnuler={() => setEdition(false)}
              onValider={async (v) => {
                await lire(
                  api.soins[':id'].$patch({
                    param: { id: s.id },
                    json: corps(soinModification, v),
                  }),
                )
                await rafraichir()
                setEdition(false)
              }}
            />
          )}
        </section>
        <Remboursements soin={s} organismes={tiers.filter((t) => t.role === 'organisme_sante')} />
        <PiecesDuSoin soin={s} foyerId={foyer?.id ?? null} />
      </div>
    </main>
  )
}

function Remboursements({
  soin,
  organismes,
}: {
  soin: FicheSoin
  organismes: { id: string; nom: string }[]
}) {
  const client = useQueryClient()
  const [recevoir, setRecevoir] = useState<string | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)
  const rafraichir = () => client.invalidateQueries()
  async function action(id: string, a: 'refuser' | 'sans_objet' | 'rouvrir') {
    setErreur(null)
    try {
      await lire(api.remboursements[':id'].action.$post({ param: { id }, json: { action: a } }))
      await rafraichir()
    } catch (e) {
      setErreur(messageErreur(e))
    }
  }
  return (
    <Section
      titre="Remboursements"
      libelleAjout="Ajouter"
      ajout={(fermer) => (
        <Formulaire
          champs={[
            {
              nom: 'payeur',
              libelle: 'Qui rembourse',
              type: 'choix',
              requis: true,
              options: PAYEURS,
            },
            {
              nom: 'organismeId',
              libelle: 'Organisme',
              type: 'choix',
              options: organismes.map((t) => ({ valeur: t.id, libelle: t.nom })),
            },
            { nom: 'montantAttenduCts', libelle: 'Montant attendu (€)', type: 'montant' },
          ]}
          onAnnuler={fermer}
          onValider={async (v) => {
            await lire(
              api.remboursements.$post({
                json: corps(remboursementCreation, { ...v, soinId: soin.id }),
              }),
            )
            await rafraichir()
            fermer()
          }}
        />
      )}
    >
      {soin.remboursements.length === 0 && (
        <Vide>Aucun : renseigner les couvertures du membre, ou en ajouter un.</Vide>
      )}
      {erreur && <p className="alerte">{erreur}</p>}
      {soin.remboursements.map((r) => {
        const recu =
          r.statut === 'recu' || r.paiementStatut === 'paye' || r.paiementStatut === 'rapproche'
        return (
          <div key={r.id} className="ligne">
            <div className="ligne-contenu">
              <span className="ligne-titre">
                <strong>
                  {LIBELLES_PAYEUR_SANTE[r.payeur]}
                  {r.organisme && ` : ${r.organisme}`}
                </strong>
                <span className={`badge ${recu ? 'ton-ok' : ''}`}>
                  {recu && r.statut !== 'recu'
                    ? 'Reçu (relevé)'
                    : LIBELLES_STATUT_REMBOURSEMENT[r.statut]}
                </span>
              </span>
              <span className="discret">
                {[
                  r.montantAttenduCts !== null && `attendu ${formaterMontant(r.montantAttenduCts)}`,
                  r.montantRecuCts !== null && `reçu ${formaterMontant(r.montantRecuCts)}`,
                  r.recuLe && `le ${jourCourt(r.recuLe)}`,
                  r.demandeLe && `demandé le ${jourCourt(r.demandeLe)}`,
                ]
                  .filter(Boolean)
                  .join(' · ')}
                {r.pieceId && (
                  <>
                    {' · '}
                    <Link to="/pieces/$id" params={{ id: r.pieceId }}>
                      pièce
                    </Link>
                  </>
                )}
              </span>
              {recevoir === r.id && (
                <Formulaire
                  enLigne
                  champs={[
                    {
                      nom: 'montantCts',
                      libelle: 'Montant reçu (€)',
                      type: 'montant',
                      requis: true,
                    },
                    { nom: 'date', libelle: 'Reçu le', type: 'date' },
                  ]}
                  libelleBouton="Noter reçu"
                  onAnnuler={() => setRecevoir(null)}
                  onValider={async (v) => {
                    await lire(
                      api.remboursements[':id'].action.$post({
                        param: { id: r.id },
                        json: corps(actionRemboursement, {
                          action: 'recevoir',
                          montantCts: v.montantCts,
                          ...(v.date ? { date: v.date } : {}),
                        }),
                      }),
                    )
                    await rafraichir()
                    setRecevoir(null)
                  }}
                />
              )}
            </div>
            <div className="ligne-actions">
              {(r.statut === 'attendu' || r.statut === 'demande' || r.statut === 'refuse') &&
                !recu &&
                recevoir !== r.id && (
                  <button
                    type="button"
                    className="bouton-discret"
                    onClick={() => setRecevoir(r.id)}
                  >
                    Reçu
                  </button>
                )}
              {(r.statut === 'attendu' || r.statut === 'demande') && (
                <button
                  type="button"
                  className="bouton-discret"
                  onClick={() => action(r.id, 'refuser')}
                >
                  Refusé
                </button>
              )}
              {r.statut !== 'sans_objet' && r.statut !== 'recu' && (
                <button
                  type="button"
                  className="bouton-discret"
                  onClick={() => action(r.id, 'sans_objet')}
                >
                  Sans objet
                </button>
              )}
              {r.statut !== 'attendu' && (
                <button
                  type="button"
                  className="bouton-discret"
                  onClick={() => action(r.id, 'rouvrir')}
                >
                  Rouvrir
                </button>
              )}
            </div>
          </div>
        )
      })}
    </Section>
  )
}

function PiecesDuSoin({ soin, foyerId }: { soin: FicheSoin; foyerId: string | null }) {
  const client = useQueryClient()
  const pieces = useQuery({
    ...requetes.pieces({ vue: 'toutes', ...(foyerId && { entiteId: foyerId }) }),
    enabled: foyerId !== null,
  })
  const [erreur, setErreur] = useState<string | null>(null)
  const dejaLiees = new Set(soin.pieces.map((p) => p.pieceId))
  return (
    <Section
      titre="Pièces"
      libelleAjout="Lier une pièce"
      ajout={(fermer) => (
        <Formulaire
          champs={[
            {
              nom: 'pieceId',
              libelle: 'Pièce du foyer',
              type: 'choix',
              requis: true,
              options: (pieces.data ?? [])
                .filter((p) => !dejaLiees.has(p.id))
                .slice(0, 200)
                .map((p) => ({
                  valeur: p.id,
                  libelle: `${p.dateDocument ? `${jourCourt(p.dateDocument)} · ` : ''}${p.nomFichier ?? p.nomOriginal ?? 'Pièce'}`,
                })),
            },
            { nom: 'role', libelle: 'C’est', type: 'choix', requis: true, options: ROLES },
          ]}
          initial={{ role: 'facture' }}
          onAnnuler={fermer}
          onValider={async (v) => {
            await lire(
              api.soins[':id'].pieces.$post({
                param: { id: soin.id },
                json: corps(lienPieceSoin, v),
              }),
            )
            await client.invalidateQueries({ queryKey: ['sante'] })
            fermer()
          }}
        />
      )}
    >
      <p className="discret">
        Factures, ordonnances et décomptes : le dossier pour l’autre parent les réunit en un PDF.
        Les décomptes lus sont reportés seuls.
      </p>
      {erreur && <p className="alerte">{erreur}</p>}
      {soin.pieces.length === 0 && <Vide>Aucune pièce liée.</Vide>}
      {soin.pieces.map((p) => (
        <div key={p.pieceId} className="ligne">
          <div className="ligne-contenu">
            <Link to="/pieces/$id" params={{ id: p.pieceId }}>
              {p.nom}
            </Link>
            <span className="discret">{LIBELLES_ROLE_PIECE_SOIN[p.role]}</span>
          </div>
          <div className="ligne-actions">
            <button
              type="button"
              className="bouton-discret"
              onClick={async () => {
                setErreur(null)
                try {
                  await lire(
                    api.soins[':id'].delier.$post({
                      param: { id: soin.id },
                      json: { pieceId: p.pieceId },
                    }),
                  )
                  await client.invalidateQueries({ queryKey: ['sante'] })
                } catch (e) {
                  setErreur(messageErreur(e))
                }
              }}
            >
              Retirer
            </button>
          </div>
        </div>
      ))}
    </Section>
  )
}
